// ============================================================
// MANIC MEETER — Meeting Generation & Configuration (Browser)
// ============================================================

import {
  Meeting, MeetingType, ColleagueRole,
} from "./types";
import { generateColleagues, resetNamePool } from "./colleagues";
import { meetingTypeDescription } from "./content";

export interface MeetingConfig {
  minParticipants: number;
  maxParticipants: number;
  baseRounds: number;     // full rounds (everyone speaks once per round)
  mustInclude?: ColleagueRole[];
  /** Filled from the active locale — the descriptions are text, not balance. */
  description: string;
  timeOfDay: string;
}

type MeetingSpec = Omit<MeetingConfig, "description">;

const MEETING_SPECS: Record<MeetingType, MeetingSpec> = {
  [MeetingType.DailyStandup]: {
    minParticipants: 4,
    maxParticipants: 8,
    baseRounds: 2,
    mustInclude: [ColleagueRole.ScrumMaster],
    timeOfDay: "09:00",
  },
  [MeetingType.Planning]: {
    minParticipants: 5,
    maxParticipants: 12,
    baseRounds: 3,
    mustInclude: [ColleagueRole.ProductOwner, ColleagueRole.ScrumMaster],
    timeOfDay: "10:00",
  },
  [MeetingType.LunchMeeting]: {
    minParticipants: 3,
    maxParticipants: 6,
    baseRounds: 2,
    timeOfDay: "12:00",
  },
  [MeetingType.OneOnOne]: {
    minParticipants: 1,
    maxParticipants: 1,
    baseRounds: 3,
    mustInclude: [ColleagueRole.Teamleiter],
    timeOfDay: "14:00",
  },
  [MeetingType.Scrum]: {
    minParticipants: 5,
    maxParticipants: 10,
    baseRounds: 3,
    mustInclude: [ColleagueRole.ScrumMaster],
    timeOfDay: "11:00",
  },
  [MeetingType.Schulung]: {
    minParticipants: 6,
    maxParticipants: 15,
    baseRounds: 4,
    mustInclude: [ColleagueRole.HRManager],
    timeOfDay: "15:00",
  },
  [MeetingType.Retro]: {
    minParticipants: 4,
    maxParticipants: 8,
    baseRounds: 2,
    mustInclude: [ColleagueRole.ScrumMaster],
    timeOfDay: "16:00",
  },
  [MeetingType.AllHands]: {
    minParticipants: 10,
    maxParticipants: 20,
    baseRounds: 3,
    mustInclude: [ColleagueRole.Chef, ColleagueRole.Abteilungsleiter],
    timeOfDay: "13:00",
  },
  [MeetingType.Brainstorming]: {
    minParticipants: 4,
    maxParticipants: 8,
    baseRounds: 3,
    mustInclude: [ColleagueRole.MarketingGuru],
    timeOfDay: "11:30",
  },
  [MeetingType.StatusUpdate]: {
    minParticipants: 3,
    maxParticipants: 6,
    baseRounds: 2,
    mustInclude: [ColleagueRole.Abteilungsleiter],
    timeOfDay: "10:30",
  },
  [MeetingType.Vorstandsmeeting]: {
    minParticipants: 8,
    maxParticipants: 15,
    baseRounds: 5,
    mustInclude: [ColleagueRole.Chef, ColleagueRole.Abteilungsleiter, ColleagueRole.HRManager],
    timeOfDay: "09:00",
  },
};

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function getMeetingConfig(type: MeetingType): MeetingConfig {
  return { ...MEETING_SPECS[type], description: meetingTypeDescription(type) };
}

export function generateMeeting(type: MeetingType, day: number): Meeting {
  const config = MEETING_SPECS[type];
  const participantCount = randInt(config.minParticipants, config.maxParticipants);

  resetNamePool();
  const participants = generateColleagues(participantCount, config.mustInclude);

  // Build turn order: indices 0..N-1 = colleague indices, N = player
  const playerSlot = participants.length; // player is the last "slot number"
  const totalSlots = participants.length + 1;
  const turnOrder: number[] = [];
  // Insert player at a random seat
  const playerSeatPos = randInt(0, totalSlots - 1);
  let colIdx = 0;
  for (let i = 0; i < totalSlots; i++) {
    if (i === playerSeatPos) {
      turnOrder.push(playerSlot);
    } else {
      turnOrder.push(colIdx);
      colIdx++;
    }
  }

  return {
    type,
    participants,
    roundsTotal: config.baseRounds,
    roundsRemaining: config.baseRounds,
    currentSpeakerIdx: 0,
    turnOrder,
    playerSlot,
    directionForward: true,
    day,
    isOnline: false,
  };
}

export function generateDaySchedule(day: number): MeetingType[] {
  if (day === 1) {
    return [MeetingType.DailyStandup, MeetingType.Planning];
  }
  const normalTypes = Object.values(MeetingType).filter((t) => t !== MeetingType.Vorstandsmeeting);
  const meetingCount = Math.min(3 + Math.floor(day / 2), 7);
  const schedule: MeetingType[] = [MeetingType.DailyStandup];
  for (let i = 1; i < meetingCount; i++) {
    schedule.push(normalTypes[randInt(0, normalTypes.length - 1)]);
  }
  // Boss encounter every 5th day
  if (day % 5 === 0) {
    schedule.push(MeetingType.Vorstandsmeeting);
  }
  return schedule;
}
